import { NgModule } from '@angular/core';
import {
  NoPreloading,
  PreloadingStrategy, Route,
  RouterModule,
  Routes
} from '@angular/router';
import {
  DisplayLoaderComponent,
  eligibilityDataCollectMatchGuard,
  ProductVerificationGuard,
  SequentialGuardMode,
} from '@brightside-web/shared/desktop';
import { loginLegalDocsMatchGuard } from '@brightside-web/shared/legal-documents';

import { VerifyComponent } from './verify/verify.component';

//NOTE: all canLoad guards here must also be applied as canActivate guards in the modules
// see https://stackoverflow.com/questions/43933595/angular2-authguard-not-working-when-using-browser-navigation-buttons
// TODO: replace all canLoad w/ canMatch and remove duplicate canLoad/canActivate
import {
  auth0LoginGuard, authCanActivateGuard, authMatchGuard,
  BsCacheService,
  chatLinkGuard,
  FeatureFlagKillSwitchGuard,
  routePath, ssoLoginGuard,
  stripAccountsPrefix
} from '@brightside-web/desktop/data-access/core-services';
import { ReviewAppGuard } from './review-app.guard';
import { WaitlistComponent } from './waitlist/waitlist.component';

import { LayoutStyle } from './routing.service';

import {
  BREADCRUMB_LABEL as INVITE_BREADCRUMB_LABEL,
  ROUTES as INVITE_ROUTES,
  ROUTES_PATH as INVITE_ROUTES_PATH,
} from './routes/invite';

import { KnownFeatureFlags } from '@brightside-web/desktop/data-access/shared';
import { SharedDisplayWaitingComponent } from '@brightside-web/shared/display';

import {
  maintenanceCanMatchGuard,
  MaintenanceProductType,
  maintenanceRedirectGuard
} from '@brightside-web/desktop/feature/maintenance';
import { ExternalLendersGuard } from '@brightside-web/desktop/feature/external-lenders';

import { FinancialCoreKYCVerificationInProgressComponent } from '@brightside-web/desktop/feature/financial-accounts-core';

import { SaviSplashComponent } from '@brightside-web/micro/entry';

import { onboardingMatchCompleteGuard } from '@brightside-web/desktop/feature/onboarding2';
import { guidGuard } from '@brightside-web/desktop/feature/onboarding2';
import { SurveyQuestionnaireService } from '@brightside-web/desktop/feature/survey-questionnaire';
import { Observable, of } from 'rxjs';
import { hardshipRoutes } from '@brightside-web/desktop/feature/hardship';
import { campaignsRoutes } from '@brightside-web/desktop/feature/campaigns';

export class SelectivePreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // ✅ Preload everything unless explicitly disabled
    if (route.data && route.data['preload'] === false) {
      return of(null); // skip this one
    }
    return load(); // preload by default
  }
}

const microAppRoutes = [
  {
    path: routePath('my_spending'),
    data: { breadcrumb: { skip: true } },
    loadChildren: () => import('@brightside-web/micro/financial-snapshot').then((m) => m.MicroFinancialSnapshotModule),
    canMatch: [authMatchGuard],
  },
  {
    path: routePath('helping_hands'),
    data: { product: MaintenanceProductType.HelpingHands, breadcrumb: { skip: true } },
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
    loadChildren: () => import('@brightside-web/micro/helping-hands').then((m) => m.MicroHelpingHandsModule),
  },
  {
    path: routePath('mpap'),
    data: { product: MaintenanceProductType.Mpap, breadcrumb: { skip: true }, preload: false },
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
    loadChildren: () => import('@brightside-web/micro/mpap').then((m) => m.MicroMpapModule),
  },
  {
    path: 'terms-update',
    data: { product: MaintenanceProductType.TermsUpdate, breadcrumb: { skip: true }, appDisplayStyle: LayoutStyle.FULL_SCREEN },
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
    loadChildren: () => import('@brightside-web/micro/terms-update').then((m) => m.MicroTermsUpdateModule),
  },
  {
    //Please pass the forwarding path in the query param like: ?pageOnForwardCtaPath=<route>
    path: 'loader',
    component: DisplayLoaderComponent,
    data: {
      breadcrumb: { skip: true },
      pageConfig: {
        fbPage: 'loader',
        fbCategory: 'pls-loader',
        pageOnForwardCtaPath: [],
      },
    },
  },
  {
    path: 'financialsnapshot-loader',
    component: DisplayLoaderComponent,
    data: {
      breadcrumb: { skip: true },
      pageConfig: {
        fbPage: 'loader',
        fbCategory: 'financial-snapshot',
        pageOnForwardCtaPath: ['financialsnapshot', 'intro'],
      },
    },
  },
  //below are to be removed in the near future
  {
    path: 'student-loans/savi',
    loadComponent: () => import('@brightside-web/micro/entry').then((c) => c.SaviInfoComponent),
  },
  {
    path: 'student-loans',
    component: SaviSplashComponent,
    canMatch: [authMatchGuard, onboardingMatchCompleteGuard],
  }
];

const accountRoutes: Routes = [
  {
    path: 'accounts',
    canMatch: [authMatchGuard, onboardingMatchCompleteGuard, maintenanceRedirectGuard],
    children: [
      {
        path: stripAccountsPrefix('savings'),
        loadChildren: () =>
          import('@brightside-web/desktop/feature/savings-account').then((m) => m.DesktopFeatureSavingsAccountModule),
        canMatch: [maintenanceCanMatchGuard],
        data: {
          breadcrumb: 'SAVINGS',
          product: MaintenanceProductType.Savings,
        },
      },
      {
        path: stripAccountsPrefix('spending'),
        loadChildren: () =>
          import('@brightside-web/desktop/feature/spending-account').then((m) => m.DesktopFeatureSpendingAccountModule),
        canMatch: [maintenanceCanMatchGuard],
        data: {
          breadcrumb: 'SPENDING',
          product: MaintenanceProductType.Spending,
        },
      },
      {
        path: stripAccountsPrefix('external'),
        loadChildren: () =>
          import('@brightside-web/desktop/feature/external-accounts').then((m) => m.DesktopFeatureExternalAccountsModule),
        canMatch: [maintenanceCanMatchGuard],
        data: {
          product: MaintenanceProductType.External,
        },
      },
      {
        path: 'transfer-funds',
        loadChildren: () =>
          import('@brightside-web/desktop-v3/feature/transfer-funds').then((m) => m.transferFundsRoutes),
        canMatch: [maintenanceCanMatchGuard],
      },
      {
        path: stripAccountsPrefix('loan_application'),
        loadChildren: () => import('@brightside-web/desktop/feature/loans').then((m) => m.DesktopFeatureLoansModule),
        canMatch: [maintenanceCanMatchGuard],
        data: {
          product: MaintenanceProductType.Loans,
        },
      },
    ],
  },
];
const routes: Routes = [
  ...microAppRoutes,
  ...accountRoutes,
  ...hardshipRoutes,
  ...campaignsRoutes,
  {
    path: 'notifications',
    canMatch: [authMatchGuard, onboardingMatchCompleteGuard, maintenanceRedirectGuard],
    loadComponent: () =>
      import('@brightside-web/desktop/feature/service-notifications').then(
        (c) => c.ServiceNotificationsComponent
      ),
  },
  {
    path: routePath('lender_alternatives'),
    loadChildren: () =>
      import('@brightside-web/desktop/feature/external-lenders').then((m) => m.DesktopFeatureExternalLendersModule),
    canMatch: [authMatchGuard, onboardingMatchCompleteGuard, maintenanceRedirectGuard],
    canLoad: [ExternalLendersGuard, FeatureFlagKillSwitchGuard],
    canActivate: [ExternalLendersGuard, FeatureFlagKillSwitchGuard],
    data: {
      featureFlagInfo: {
        flag: KnownFeatureFlags.ENABLELENDERALTERNATIVES,
      },
    },
  },
  {
    path: 'maintenance',
    loadChildren: () => import('@brightside-web/desktop/feature/maintenance').then((m) => m.DesktopFeatureMaintenanceModule),
  },
  {
    path: 'chathistory',
    loadChildren: () => import('@brightside-web/desktop/feature/chat-history').then((m) => m.DesktopFeatureChatHistoryModule),
    canMatch: [authMatchGuard, onboardingMatchCompleteGuard],
  },
  {
    path: 'eligibility',
    loadChildren: () =>
      import('@brightside-web/desktop/feature/user-eligibility').then((m) => m.DesktopFeatureUserEligibilityModule),
  },
  {
    path: 'registration',
    loadChildren: () => import('@brightside-web/desktop/feature/registration').then((m) => m.DesktopFeatureRegistrationModule),
  },
  {
    path: 'onboarding',
    loadChildren: () => import('@brightside-web/desktop/feature/onboarding2').then((m) => m.onboardingRoutes),
    // Direct onboarding navigation must collect missing eligibility data (notably last-four
    // SSN) before the client enters onboarding. The same guard on `home` only covers clients
    // whose post-login navigation attempts that route first.
    canMatch: [authMatchGuard, eligibilityDataCollectMatchGuard],
    data: { appDisplayStyle: LayoutStyle.FULL_SCREEN },
  },
  {
    path: routePath('credit'),
    loadChildren: () => import('@brightside-web/desktop/feature/credit').then((m) => m.DesktopFeatureCreditModule),
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard],
    data: { product: MaintenanceProductType.Credit, breadcrumb: { label: 'Home', routeInterceptor: () => '/home' } },
  },
  {
    path: 'credit-setup',
    loadChildren: () => import('@brightside-web/desktop/feature/credit-setup').then((m) => m.DesktopFeatureCreditSetupModule),
    // eligibilityDataCollectMatchGuard is here as well as on `home` because credit setup leads
    // to the identity (DIT) step, which the API rejects when there is no last four SSN on the
    // client record. Reaching it by bookmark, deep link or refresh skips `home` entirely, which
    // is how clients still hit that failure after MW-10702 was first fixed.
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, eligibilityDataCollectMatchGuard],
    canActivateChild: [FeatureFlagKillSwitchGuard, ProductVerificationGuard],
    data: {
      breadcrumb: { label: 'Home', routeInterceptor: () => '/home' },
      featureFlagInfo: {
        flag: KnownFeatureFlags.CREDITSETUP,
      },
      product: MaintenanceProductType.Credit,
    },
    resolve: {
      appDisplayStyle: 'CreditLayoutResolver',
    },
  },
  {
    path: 'home',
    loadChildren: () => import('@brightside-web/desktop/feature/home').then((m) => m.DesktopFeatureHomeModule),
    // Order decides which redirect wins, and it encodes the intended post-login sequence:
    // login legal documents, then eligibility, then onboarding. Both of the first two have to
    // sit ahead of onboardingMatchCompleteGuard, which redirects to /onboarding at match time
    // — that is how clients skipped the eligibility check (MW-10702), and pending terms with
    // it. maintenanceRedirectGuard leads the sequencing guards so a maintenance window takes
    // precedence over either redirect.
    //
    // Ordering only picks the winning result: Angular subscribes every canMatch guard
    // concurrently, so each one still issues its request even when an earlier guard redirects.
    // eligibilityDataCollectMatchGuard short-circuits on the hasCheckedEligibilityDataCollect
    // cache key after its first clean pass. loginLegalDocsMatchGuard has no equivalent key and
    // is not cached, so it calls /legal-docs/packet/login/step on each match. See docs/NOTES.md.
    canMatch: [
      authMatchGuard,
      guidGuard,
      maintenanceRedirectGuard,
      loginLegalDocsMatchGuard,
      eligibilityDataCollectMatchGuard,
      onboardingMatchCompleteGuard,
    ],
    data: {
      breadcrumb: 'Home',
      appDisplayStyle: LayoutStyle.DEFAULT,
      guardsRelation: SequentialGuardMode.AND,
    },
  },
  {
    //https://netbasal.com/give-your-modals-url-address-with-auxiliary-routes-in-angular-eb76497c0bca
    path: 'settings',
    outlet: 'modal',
    loadChildren: () => import('@brightside-web/desktop/feature/settings').then((m) => m.DesktopFeatureSettingsModule),
    canMatch: [authMatchGuard],
  },
  {
    path: 'ways-we-can-help',
    canMatch: [authMatchGuard],
    loadChildren: () => import('@brightside-web/desktop/feature/wwch').then((m) => m.DesktopFeatureWwchModule),
    data: { breadcrumb: 'Ways we can help' },
  },
  {
    path: 'verification',
    loadChildren: () => import('@brightside-web/desktop/feature/verification').then((m) => m.DesktopFeatureVerificationModule),
  },
  {
    path: 'program',
    canMatch: [authMatchGuard, onboardingMatchCompleteGuard],
    loadChildren: () => import('@brightside-web/desktop/feature/programs').then((m) => m.DesktopFeatureProgramsModule),
  },
  {
    path: routePath('survey'),
    loadChildren: () =>
      import('@brightside-web/desktop/feature/survey-questionnaire').then((m) => m.DesktopFeatureSurveyQuestionnaireModule),
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
    data: { product: MaintenanceProductType.Survey },
    providers: [SurveyQuestionnaireService],
  },
  {
    path: routePath('contest'),
    loadChildren: () =>
      import('@brightside-web/desktop/feature/prize-link-savings').then((m) => m.DesktopFeaturePrizeLinkSavingsModule),
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
    data: { product: MaintenanceProductType.Contest },
  },
  {
    path: 'contest-legal/sweepstakes-amoe',
    loadComponent: () => import('@brightside-web/desktop/feature/prize-link-savings').then((c) => c.SweepstakesAmoeComponent),
    data: { appDisplayStyle: LayoutStyle.PUBLIC_ACCESS },
  },
  {
    path: routePath('find_money'),
    loadChildren: () => import('@brightside-web/desktop/feature/find-money').then((m) => m.DesktopFeatureFindMoneyModule),
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
    data: { product: MaintenanceProductType.FindMoney },
  },
  {
    path: routePath('my_spending_web'),
    loadChildren: () => import('@brightside-web/desktop/feature/my-spending').then((m) => m.MySpendingModule),
    canMatch: [authMatchGuard, maintenanceCanMatchGuard, maintenanceRedirectGuard, onboardingMatchCompleteGuard],
  },
  {
    path: INVITE_ROUTES_PATH,
    canMatch: [authMatchGuard],
    canActivate: [authCanActivateGuard],
    children: INVITE_ROUTES,
    data: { breadcrumb: INVITE_BREADCRUMB_LABEL },
  },
  { path: 'sign_in', redirectTo: 'registration/sign_in' },
  { path: 'get_started', redirectTo: 'registration/create-account' },
  { path: 'verify', component: VerifyComponent },
  { path: 'waitlist', component: WaitlistComponent, data: { appDisplayStyle: LayoutStyle.PUBLIC_ACCESS } },
  {
    path: 'review-app',
    canActivate: [ReviewAppGuard],
    component: DisplayLoaderComponent,
    data: { appDisplayStyle: LayoutStyle.PUBLIC_ACCESS },
  },
  {
    path: 'setupcredit',
    redirectTo: 'credit-setup/creation',
  },
  {
    path: 'savings' /* the default card route ? */,
    redirectTo: 'home/savings',
  },
  {
    path: 'contestintro',
    redirectTo: 'pls/contest-intro',
  },
  {
    path: 'loans/dashboard',
    redirectTo: routePath('loan_dashboard'),
  },
  {
    path: 'authenticating',
    component: SharedDisplayWaitingComponent,
    data: {
      appDisplayStyle: LayoutStyle.FULL_SCREEN,
      config: {
        pageHideBackButton: true,
        pageOnForwardCtaPath: ['home','legal']
      },
    },
  },
  {
    path: 'kyc-verification',
    component: FinancialCoreKYCVerificationInProgressComponent,
  },
  { path: 'sso-login',
    canActivate: [ssoLoginGuard],
    component: SharedDisplayWaitingComponent,
    data: {
      appDisplayStyle: LayoutStyle.HIDE_EVERYTHING,
      config: {
        pageHideBackButton: true
      }
    }
  },
  {
    path: 'auth',
    children: [
      {
        path: ':loginType',
        canActivate: [auth0LoginGuard],
        component: SharedDisplayWaitingComponent,
        data: {
          appDisplayStyle: LayoutStyle.HIDE_EVERYTHING,
          config: {
            pageHideBackButton: true
          }
        }
      }
    ]
  },
  { path: 'chat', canActivate: [chatLinkGuard], children: [] },
  { path: '**', redirectTo: 'registration/create-account' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: NoPreloading,
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: 'CreditLayoutResolver',
      useFactory: creditLayoutResolverFactory,
    },
    SelectivePreloadingStrategy
  ],
})
export class AppRoutingModule {}

export function creditLayoutResolverFactory() {
  // This will return a function that returns a promise
  return () =>
    BsCacheService.getItemStatic('ONBOARDING_FLOW_ACTIVE').then((active: any) =>
      active === true ? LayoutStyle.FULL_SCREEN : LayoutStyle.DEFAULT
    );
}

